import { useRef, useCallback } from 'react';
import useSettingsState from './use-settings-state';
import useFormValidation from './use-form-validation';
import useStageHandlers from './use-stage-handlers';

const useSettingsManager = ({ initialData = null, checkPersisting }) => {
  const mountedChunks = useRef(initialData?.mountedChunks || new Set());
  const tabToChunksMapping = useRef(initialData?.tabToChunksMapping || new Map());

  const settingsState = useSettingsState(initialData?.settingsState);
  const formValidation = useFormValidation(initialData?.formValidation);
  const stageHandlers = useStageHandlers(initialData?.stageHandlers);

  const unmountChunk = useCallback(
    (chunkId) => {
      if (checkPersisting?.()) {
        // Block unmount during persistence to prevent data loss.
        // While persisting, we capture live references (Sets, Maps, state objects)
        // that unmount would clear, making the shared data outdated.
        return;
      }

      formValidation.unregisterDefaultValues(chunkId);
      settingsState.clearStateByChunkId(chunkId);
      stageHandlers.unregisterStageHandlers(chunkId);

      tabToChunksMapping.current.forEach((chunks) => {
        chunks.delete(chunkId);
      });

      mountedChunks.current.delete(chunkId);
    },
    [checkPersisting, formValidation, settingsState, stageHandlers]
  );

  const mountChunk = useCallback(
    async (chunkId, config) => {
      if (mountedChunks.current.has(chunkId)) {
        // eslint-disable-next-line no-console
        console.warn(`Chunk ${chunkId} is already mounted. Skipping duplicate mount.`);
        return;
      }

      try {
        if (config.initialState) {
          settingsState.registerInitialState(chunkId, config.initialState);
        }

        if (config.defaultValues) {
          formValidation.registerDefaultValues(chunkId, config.defaultValues);
        }

        if (config.stageHandlers) {
          stageHandlers.registerStageHandlers(chunkId, config.stageHandlers);
        }

        if (config.tabId) {
          const existingChunks = tabToChunksMapping.current.get(config.tabId) || new Set();
          existingChunks.add(chunkId);
          tabToChunksMapping.current.set(config.tabId, existingChunks);
        }

        mountedChunks.current.add(chunkId);
      } catch (error) {
        unmountChunk(chunkId);
        throw error;
      }
    },
    [settingsState, formValidation, stageHandlers, unmountChunk]
  );

  const isChunkMounted = useCallback((chunkId) => mountedChunks.current.has(chunkId), []);

  const getTabIdByChunkId = useCallback((chunkId) => {
    const entry = Array.from(tabToChunksMapping.current.entries()).find(([, chunks]) =>
      chunks.has(chunkId)
    );

    return entry ? entry[0] : null;
  }, []);

  const getPersistData = useCallback(
    () => ({
      settingsState: settingsState.getPersistData(),
      formValidation: formValidation.getPersistData(),
      stageHandlers: stageHandlers.getPersistData(),
      mountedChunks: mountedChunks.current,
      tabToChunksMapping: tabToChunksMapping.current,
    }),
    [settingsState, formValidation, stageHandlers]
  );

  return {
    state: {
      data: settingsState.state,
      updateState: settingsState.updateState,
      getStateDiff: settingsState.getStateDiff,
      getInitialState: settingsState.getInitialState,
      clearStateByChunkId: settingsState.clearStateByChunkId,
      getModifiedAttributes: settingsState.getModifiedAttributes,
      getLastModifiedAttributes: settingsState.getLastModifiedAttributes,
    },
    form: {
      control: formValidation.control,
      trigger: formValidation.trigger,
      triggerFormValidation: formValidation.triggerFormValidation,
      triggerFieldValidation: formValidation.triggerFieldValidation,
      clearErrors: formValidation.clearErrors,
      manageFields: formValidation.manageFields,
      getDefaultValues: formValidation.getDefaultValues,
      getValues: formValidation.getValues,
      getChunkIdByFieldName: formValidation.getChunkIdByFieldName,
      setValue: formValidation.setValue,
      setFocus: formValidation.setFocus,
    },
    stageHandlers: {
      runStage: stageHandlers.runStage,
    },
    mountChunk,
    unmountChunk,
    isChunkMounted,
    getTabIdByChunkId,
    getPersistData,
  };
};

export default useSettingsManager;
